// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import styles from '@app/features/ui/character_counter/CharacterCounter.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

interface CharacterCounterProps {
	currentLength: number;
	maxLength: number;
	canUpgrade: boolean;
	premiumMaxLength: number;
	onUpgradeClick: () => void;
	className?: string;
}
export const CharacterCounter = observer(
	({currentLength, maxLength, canUpgrade, premiumMaxLength, onUpgradeClick, className}: CharacterCounterProps) => {
		const {i18n} = useLingui();
		const remaining = maxLength - currentLength;
		const isOverLimit = remaining < 0;
		const isNearingLimit = remaining < 50;
		const showPremiumFeatures = shouldShowPremiumFeatures();
		const needsPremium = canUpgrade && showPremiumFeatures && (isNearingLimit || isOverLimit);
		const tooltipText = needsPremium
			? i18n._(
					msg`${remaining} characters left. Get ${PREMIUM_PRODUCT_NAME} to write up to ${premiumMaxLength} characters.`,
				)
			: isOverLimit
				? i18n._(msg`Message is too long`)
				: i18n._(msg`${remaining} characters left`);
		if (needsPremium) {
			return (
				<Tooltip text={tooltipText}>
					<FocusRing offset={-2}>
						<button
							type="button"
							onClick={onUpgradeClick}
							className={clsx(
								styles.counterButton,
								isOverLimit || isNearingLimit ? styles.textDanger : styles.textTertiary,
								className,
							)}
						>
							{remaining}
						</button>
					</FocusRing>
				</Tooltip>
			);
		}
		return (
			<Tooltip text={tooltipText}>
				<span
					className={clsx(
						styles.counterSpan,
						isOverLimit || isNearingLimit ? styles.textDanger : styles.textTertiary,
						className,
					)}
				>
					{remaining}
				</span>
			</Tooltip>
		);
	},
);
